import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Box,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  TableSortLabel,
  Card,
  CircularProgress
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { Download, Eye, Search } from 'lucide-react';
import { adminAPI, eventBatchAPI, trekAPI } from '../services/api';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import useListQueryParams from '../hooks/useListQueryParams';
import { PageHeader, FilterBar, EmptyState } from '../components/ui';
import { formatAdminInr } from '../utils/formatAdminPrice.js';
import EditRegistrationModal from '../components/EditRegistrationModal.jsx';
import EditTicketGroupModal from '../components/EditTicketGroupModal.jsx';
import RegistrationStatusHover from '../components/RegistrationStatusHover.jsx';
import {
  REGISTRATION_STATUS_FILTER_ALL as STATUS_FILTER_ALL,
  REGISTRATION_STATUS_OPTIONS,
  registrationStatusLabel
} from '../constants/registrationStatuses.js';

const ALLOWED_STATUS_FILTER_VALUES = new Set(REGISTRATION_STATUS_OPTIONS.map((o) => o.value));
const DEFAULT_RECENT_REGISTRATIONS = 10;
import { packageDetailSummary } from '../utils/packageDisplayText.js';
import { sortEventBatchesByStartDateAsc } from '../utils/sortEventBatchesByStartDateDesc.js';
import { fetchAllEventBatches } from '../utils/fetchAllEventBatches.js';
import {
  registrationBatchListParams,
  registrationBatchOptionLabel
} from '../utils/registrationPickerBatches.js';
import { isNonPayingRegistration } from '../utils/registrationStaffStatus.js';
import {
  getBookingParticipantCount,
  getEffectiveRegistrationStatus,
  getParticipantPaymentState,
  isParticipantFullyPaid,
  bookingFeeTotal,
  suggestParticipantReceiptAmount,
  resolveBookingPackage
} from '../utils/participantPaymentShare.js';
import {
  ADVANCE_WINDOW_DAYS,
  REMAINING_DUE_DAYS_BEFORE_DEPARTURE,
  effectiveBookingAdvance,
  hasPartialAdvanceStructure
} from '../utils/bookingAdvanceAmounts.js';
import { formatPersonDisplayName } from '../utils/capitalizePersonName.js';
import { RECEIPT_PAYMENT_METHOD_OPTIONS } from '../constants/receiptPaymentMethods.js';
import { resolveTicketPnrs } from '../utils/ticketPnr.js';
import { bookingHasTicketFiles, bookingInvolvesTickets, resolveTicketPackageSnapshot } from '../utils/ticketPackage.js';
import { formatBookingStayDisplay } from '../utils/formatStayOptionLabel.js';
import './Registrations.css';

function formatDate(d) {
  if (!d) return '—';
  const x = new Date(d);
  return isNaN(x.getTime()) ? '—' : x.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}


/** Registrations table: 10-digit local number (no 91 prefix in UI; CSV export unchanged on server). */
function formatPhoneForTableDisplay(raw) {
  if (raw == null || String(raw).trim() === '') return '—';
  let d = String(raw).replace(/\D/g, '');
  if (!d) return '—';
  if (d.length === 12 && d.startsWith('91')) d = d.slice(2);
  else if (d.length === 11 && d.startsWith('0')) d = d.slice(1);
  return d || '—';
}

function formatGenderDisplay(raw) {
  if (raw == null || String(raw).trim() === '') return '—';
  const s = String(raw).trim().toLowerCase();
  if (s === 'male') return 'Male';
  if (s === 'female') return 'Female';
  if (s === 'other') return 'Other';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Same rules as server CSV export (`registrationExportAgeYears`). */
function formatAgeFromBirthdate(birthdate) {
  if (birthdate == null || String(birthdate).trim() === '') return '—';
  const raw = String(birthdate).trim();
  const d = raw.length >= 10 ? new Date(`${raw.slice(0, 10)}T12:00:00`) : new Date(raw);
  if (isNaN(d.getTime())) return '—';
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age -= 1;
  if (age < 0 || age > 130) return '—';
  return String(age);
}

function primaryTravelerDob(primary, reg) {
  return (
    primary?.dateOfBirth ||
    primary?.birthdate ||
    primary?.dob ||
    reg?.dob ||
    reg?.dateOfBirth ||
    null
  );
}

function bookingPrimaryDisplayName(booking) {
  const reg = booking?.registration || null;
  const primary = Array.isArray(booking?.travelers) && booking.travelers[0] ? booking.travelers[0] : null;
  return formatPersonDisplayName(
    primary?.firstName || reg?.firstName,
    primary?.lastName || reg?.lastName
  );
}

function bookingBatchDateLabel(booking) {
  const start = booking?.batch?.startDate;
  const end = booking?.batch?.endDate;
  if (!start && !end) return '—';
  if (start && end) return `${formatDate(start)} – ${formatDate(end)}`;
  return formatDate(start || end);
}

function visibleTrekPackages(packages) {
  return (Array.isArray(packages) ? packages : [])
    .filter((pkg) => pkg && pkg.hidden !== true && pkg.hide !== true)
    .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
}

function packageFilterTitle(pkg) {
  return pkg?.name?.trim() ? pkg.name.trim() : `Package #${pkg?.id ?? '—'}`;
}

function packageFilterDetail(pkg) {
  const detail = packageDetailSummary(pkg);
  if (!detail) return '';
  return detail.length > 120 ? `${detail.slice(0, 120).trim()}…` : detail;
}

const Registrations = () => {
  const queryClient = useQueryClient();
  const { query, setQuery } = useListQueryParams({
    page: '1',
    limit: String(DEFAULT_RECENT_REGISTRATIONS),
    trekId: '',
    status: STATUS_FILTER_ALL,
    batchId: '',
    packageIds: '',
    search: '',
    showAll: '',
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });
  const rawStatusParam = String(query.status || '').trim();
  const statusSelections =
    !rawStatusParam || rawStatusParam === STATUS_FILTER_ALL
      ? []
      : [
          ...new Set(
            rawStatusParam
              .split(',')
              .map((s) => s.trim().toLowerCase())
              .filter((s) => ALLOWED_STATUS_FILTER_VALUES.has(s))
          )
        ];
  const statusQueryKey = statusSelections.length ? [...statusSelections].sort().join(',') : 'all';
  const rawPackageParam = String(query.packageIds || '').trim();
  const packageSelections = useMemo(
    () =>
      !rawPackageParam
        ? []
        : [
            ...new Set(
              rawPackageParam
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean)
            )
          ],
    [rawPackageParam]
  );
  const trekFilter = query.trekId || '';
  const batchFilter = query.batchId || '';
  const search = query.search || '';
  const showAll = query.showAll === 'true';
  const page = Number(query.page || 1);
  const limit = Number(query.limit || 10);
  const sortBy = query.sortBy || 'createdAt';
  const [isSubmitClicked, setIsSubmitClicked] = useState(false);
  const [selectedPackageNames, setSelectedPackageNames] = useState([]);
  const [selectedTransport, setSelectedTransport] = useState([]);
  const sortOrder = query.sortOrder === 'asc' ? 'asc' : 'desc';

  const toggleColumnSort = (columnKey) => {
    if (sortBy === columnKey) {
      setQuery({ sortOrder: sortOrder === 'asc' ? 'desc' : 'asc', page: '1' });
    } else {
      setQuery({ sortBy: columnKey, sortOrder: 'asc', page: '1' });
    }
  };
  const [receiptModal, setReceiptModal] = useState({
    open: false,
    booking: null,
    bookingLoading: false,
    participantName: null,
    participantIndex: null,
    participantCount: 1
  });
  const [editModal, setEditModal] = useState({ open: false, bookingId: null });
  const [ticketModal, setTicketModal] = useState({ open: false, row: null });
  const [exporting, setExporting] = useState(false);
  const [receiptForm, setReceiptForm] = useState({
    amount: '',
    notes: '',
    method: 'cash'
  });

  const { permissions, can } = useAuth();
  const canManageRegistrations =
    permissions.includes('*') || permissions.includes('registrations_manage');
  const canExportRegistrationsCsv = can('registrations_export') || can('registrations_manage');
  const canViewTickets = can('tickets') || can('tickets_manage') || canManageRegistrations;

  const batchListParams = useMemo(
    () =>
      showAll
        ? { sortBy: 'startDate', sortOrder: 'asc', noCount: 'true', pickerOnly: 'true' }
        : registrationBatchListParams(),
    [showAll]
  );

  const { data: batchesData, isFetching: batchesLoading } = useQuery({
    queryKey: ['admin-event-batches', trekFilter, showAll, batchListParams],
    queryFn: async () => {
      const base = { ...batchListParams, trekId: trekFilter };
      if (showAll) {
        const rows = await fetchAllEventBatches(base);
        return { data: { data: rows } };
      }
      return eventBatchAPI.list(base);
    },
    enabled: Boolean(trekFilter)
  });
  const allBatches = batchesData?.data?.data || batchesData?.data || [];
  const batches = sortEventBatchesByStartDateAsc(
    trekFilter ? allBatches.filter((b) => String(b.trekId || b.trek?.id) === String(trekFilter)) : []
  );

  const { data: allTreksData } = useQuery({
    queryKey: ['admin-treks-for-registrations', 'all'],
    queryFn: () => trekAPI.list({ page: 1, limit: 500 }),
    enabled: showAll
  });

  const { data: upcomingBatchesForTreksData } = useQuery({
    queryKey: ['admin-event-batches-for-trek-filter', showAll],
    queryFn: () => eventBatchAPI.list(batchListParams),
    enabled: !showAll
  });

  const upcomingTreks = useMemo(() => {
    const rows = upcomingBatchesForTreksData?.data?.data || upcomingBatchesForTreksData?.data || [];
    const byId = new Map();
    for (const b of rows) {
      const t = b.trek;
      if (t?.id == null) continue;
      const key = String(t.id);
      if (!byId.has(key)) {
        byId.set(key, { id: t.id, name: t.name, slug: t.slug });
      }
    }
    return [...byId.values()].sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
  }, [upcomingBatchesForTreksData]);

  const treks = showAll ? (allTreksData?.data?.data || []) : upcomingTreks;

  const { data: trekPackagesData, isFetching: trekPackagesLoading } = useQuery({
    queryKey: ['admin-trek-packages-for-registrations', trekFilter],
    queryFn: () => trekAPI.listPackages(trekFilter),
    enabled: Boolean(trekFilter),
    staleTime: 60_000
  });
  const trekPackages = useMemo(
    () => visibleTrekPackages(trekPackagesData?.data?.data || trekPackagesData?.data || []),
    [trekPackagesData]
  );
  const allPackageIds = useMemo(() => trekPackages.map((pkg) => String(pkg.id)), [trekPackages]);
  const validPackageSelections = useMemo(() => {
    if (!allPackageIds.length) return packageSelections;
    const allowed = new Set(allPackageIds);
    return packageSelections.filter((id) => allowed.has(id));
  }, [packageSelections, allPackageIds]);
  const packageQueryKey =
    !validPackageSelections.length || validPackageSelections.length === allPackageIds.length
      ? 'all'
      : [...validPackageSelections].sort().join(',');
  const packageFilterActive =
    Boolean(allPackageIds.length) &&
    validPackageSelections.length > 0 &&
    validPackageSelections.length < allPackageIds.length;
  const packageSelectValue = packageFilterActive ? validPackageSelections : allPackageIds;

  const isValidTrekFilter = Boolean(trekFilter && treks.some((t) => String(t.id) === String(trekFilter)));
  const isValidBatchFilter = Boolean(batchFilter && batches.some((b) => String(b.id) === String(batchFilter)));
  const hasSearch = Boolean(search.trim());
  /** Search alone loads matching rows; trek filter requires a specific event (batch). */
  const isFilteredView = hasSearch || (isValidTrekFilter && isValidBatchFilter);
  const showingRecentDefault = !isFilteredView;

  const params = {
    page: showingRecentDefault ? 1 : page,
    limit: showingRecentDefault ? DEFAULT_RECENT_REGISTRATIONS : limit,
    sortBy,
    sortOrder
  };
  if (statusSelections.length) params.status = statusSelections.join(',');
  if (isValidTrekFilter && packageFilterActive) params.packageIds = validPackageSelections.join(',');
  if (isValidTrekFilter && isValidBatchFilter) {
    params.trekId = trekFilter;
    params.batchId = batchFilter;
  }
  if (search.trim()) params.search = search.trim();

  const { data, isLoading } = useQuery({
    queryKey: [
      'admin-bookings',
      statusQueryKey,
      trekFilter,
      batchFilter,
      packageQueryKey,
      search,
      sortBy,
      sortOrder,
      showingRecentDefault ? 'recent' : page,
      showingRecentDefault ? DEFAULT_RECENT_REGISTRATIONS : limit
    ],
    queryFn: () => adminAPI.getBookings(params),
    staleTime: 30_000,
    keepPreviousData: true,
    enabled: isSubmitClicked || hasSearch
  });

  const list = data?.data?.data || [];
  const displayRows = useMemo(
    () =>
      list.flatMap((booking) => {
        const rawTravelers = Array.isArray(booking?.travelers) ? booking.travelers : [];
        const travelers = rawTravelers.filter((traveler) => {
          if (!traveler || typeof traveler !== 'object') return false;
          const hasName =
            String(traveler.firstName || '').trim() || String(traveler.lastName || '').trim();
          const hasPhone = String(traveler.phone || '').trim();
          return Boolean(hasName || hasPhone);
        });
        // One registration/booking should not expand into extra ghost rows from junk traveler JSON.
        const countCap = Math.max(1, Number(booking?.travelerCount) || 1);
        const capped =
          travelers.length > countCap ? travelers.slice(0, countCap) : travelers;
        if (!capped.length) {
          return [{ booking, traveler: null, travelerIndex: 0, rowKey: `${booking?.id ?? 'booking'}-fallback` }];
        }
        return capped.map((traveler, travelerIndex) => ({
          booking,
          traveler,
          travelerIndex,
          rowKey: `${booking?.id ?? 'booking'}-traveler-${travelerIndex}`
        }));
      }),
    [list]
  );
  const pagination = data?.data?.pagination || {};
  const { showToast } = useToast();

  const addReceiptMutation = useMutation(
    ({ bookingId, body }) => adminAPI.addBookingReceipt(bookingId, body),
    {
      onSuccess: (res) => {
        queryClient.invalidateQueries('admin-bookings');
        setReceiptModal({
          open: false,
          booking: null,
          bookingLoading: false,
          participantName: null,
          participantIndex: null,
          participantCount: 1
        });
        setReceiptForm({ amount: '', notes: '', method: 'cash' });
        const bookingData = res?.data?.data;
        const rn = Array.isArray(bookingData?.receipts) ? bookingData.receipts[0]?.receiptNumber : null;
        showToast({
          message: rn ? `Receipt saved (${rn}).` : 'Receipt saved.',
          severity: 'success'
        });
      },
      onError: (err) =>
        showToast({
          message: err.response?.data?.message || 'Failed to add receipt',
          severity: 'error'
        })
    }
  );

  const openEditRegistration = (row) => {
    if (!row?.id) {
      showToast({ message: 'Missing registration id for this row.', severity: 'warning' });
      return;
    }
    setEditModal({ open: true, bookingId: row.id });
  };

  const openTicketModal = (row) => {
    if (!row?.id) {
      showToast({ message: 'Missing booking for this row.', severity: 'warning' });
      return;
    }
    setTicketModal({
      open: true,
      row: { ...row, ticketPackage: resolveTicketPackageSnapshot(row) }
    });
  };

  const showRegistrationActions = canManageRegistrations || canViewTickets;

  const openReceiptModal = async ({ booking, traveler, travelerIndex }) => {
    const idx = Number.isFinite(travelerIndex) ? travelerIndex : 0;
    const initialPayment = getParticipantPaymentState(booking, traveler, idx);
    setReceiptModal({
      open: true,
      booking,
      bookingLoading: true,
      participantName: initialPayment.participantName || null,
      participantIndex: idx,
      participantCount: initialPayment.count
    });
    setReceiptForm({
      amount: suggestParticipantReceiptAmount(initialPayment, booking),
      notes: '',
      method: 'cash'
    });

    try {
      const res = await adminAPI.getBooking(booking.id);
      const fresh = res?.data?.data || booking;
      const payment = getParticipantPaymentState(fresh, traveler, idx);
      setReceiptModal((prev) => ({
        ...prev,
        booking: fresh,
        bookingLoading: false,
        participantName: payment.participantName || prev.participantName,
        participantCount: payment.count
      }));
      setReceiptForm((prev) => ({
        ...prev,
        amount: suggestParticipantReceiptAmount(payment, fresh) || prev.amount
      }));
    } catch {
      setReceiptModal((prev) => ({ ...prev, bookingLoading: false }));
      showToast({
        message: 'Could not refresh payment details. Amounts may be incomplete until the API is available.',
        severity: 'warning'
      });
    }
  };

  const submitReceipt = () => {
    const b = receiptModal.booking;
    if (!b?.id) return;
    const amt = Number(receiptForm.amount);
    if (!Number.isFinite(amt) || amt <= 0) {
      showToast({ message: 'Enter a valid amount.', severity: 'warning' });
      return;
    }
    const noteRaw = String(receiptForm.notes || '').trim();
    const participantLabel = receiptModal.participantName
      ? `Participant: ${receiptModal.participantName}${
          receiptModal.participantCount > 1 && Number.isFinite(receiptModal.participantIndex)
            ? ` (${Number(receiptModal.participantIndex) + 1}/${receiptModal.participantCount})`
            : ''
        }`
      : '';
    const participantPrefix = participantLabel;
    const noteWithParticipant = participantPrefix
      ? (noteRaw ? `${participantPrefix} | ${noteRaw}` : participantPrefix)
      : (noteRaw || undefined);
    addReceiptMutation.mutate({
      bookingId: b.id,
      body: {
        amount: amt,
        paidAt: new Date().toISOString(),
        notes: noteWithParticipant,
        method: receiptForm.method || 'office'
      }
    });
  };

  const buildListFilterParams = () => {
    const p = {};
    if (statusSelections.length) p.status = statusSelections.join(',');
    if (isValidTrekFilter && packageFilterActive) p.packageIds = validPackageSelections.join(',');
    if (isValidTrekFilter && isValidBatchFilter) {
      p.trekId = trekFilter;
      p.batchId = batchFilter;
    }
    if (search.trim()) p.search = search.trim();
    if (sortBy) p.sortBy = sortBy;
    if (sortOrder) p.sortOrder = sortOrder;
    return p;
  };

  /** Matches server export filename when trek + event are selected (works if CORS hides Content-Disposition). */
  const localExportFilename = () => {
    if (!isValidTrekFilter || !isValidBatchFilter) return null;
    const t = treks.find((tr) => String(tr.id) === String(trekFilter));
    const b = batches.find((bt) => String(bt.id) === String(batchFilter));
    if (!t?.name || !b?.startDate || !b?.endDate) return null;
    const d1 = new Date(b.startDate);
    const d2 = new Date(b.endDate);
    if (isNaN(d1.getTime()) || isNaN(d2.getTime())) return null;
    const fmt = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const base = `${t.name} - ${fmt(d1)} to ${fmt(d2)}`;
    const safe = base.replace(/[\\/:*?"<>|]/g, '-').replace(/\s+/g, ' ').trim().slice(0, 180);
    return `${safe || 'registrations'}.csv`;
  };

  const handleExportRegistrations = async () => {
    setExporting(true);
    try {
      const res = await adminAPI.exportRegistrationsCsv(buildListFilterParams());
      const truncated = String(res.headers['x-export-truncated'] || '').toLowerCase() === 'true';
      const clientName = localExportFilename();
      let filename = clientName;
      if (!filename) {
        const cd = res.headers['content-disposition'] || '';
        const star = cd.match(/filename\*=UTF-8''([^;]+)/i);
        if (star) {
          try {
            filename = decodeURIComponent(star[1].trim()) || null;
          } catch {
            filename = null;
          }
        }
        if (!filename) {
          const q = cd.match(/filename="([^"]+)"/i);
          if (q?.[1]) filename = q[1];
        }
        if (!filename) {
          filename = `registrations-export-${new Date().toISOString().slice(0, 10)}.csv`;
        }
      }
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      showToast({
        message: truncated
          ? 'Export downloaded (row limit reached; narrow filters for full data).'
          : 'Export downloaded.',
        severity: truncated ? 'warning' : 'success'
      });
    } catch (err) {
      const msg = err.response?.data?.message;
      if (msg) {
        showToast({ message: String(msg), severity: 'error' });
      } else if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const j = JSON.parse(text);
          showToast({ message: j.message || 'Export failed.', severity: 'error' });
        } catch {
          showToast({ message: 'Export failed.', severity: 'error' });
        }
      } else {
        showToast({ message: err.message || 'Export failed.', severity: 'error' });
      }
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="registrations-page">
      <PageHeader
        title="Registrations"
        subtitle="Select a Trek and Departure Date to view registrations, or use Global Search to find specific participants."
        actions={
          <TextField
            size="small"
            placeholder="Name, phone, reg ID…"
            value={search}
            onChange={(e) => {
              setQuery({ search: e.target.value });
              if (e.target.value) setIsSubmitClicked(true);
            }}
            sx={{ minWidth: 260, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f8fafc' } }}
            InputProps={{ startAdornment: <Search size={15} style={{ marginRight: 6, color: '#94a3b8', flexShrink: 0 }} /> }}
          />
        }
      />

      {/* ── Filter toolbar ────────────────────────────── */}
      
      <Card sx={{ p: 3, borderRadius: 3, border: '1px solid var(--border-color)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', mb: 4 }} elevation={0}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <label style={{ display: 'flex', alignItems: 'center', fontSize: '0.85rem', color: 'var(--text-secondary)', cursor: 'pointer', fontWeight: 600 }}>
            <input
              type="checkbox"
              checked={showAll}
              onChange={(e) =>
                setQuery({
                  showAll: e.target.checked ? 'true' : '',
                  trekId: '',
                  batchId: '',
                  packageIds: ''
                })
              }
              style={{ marginRight: '6px' }}
            />
            Show All Dates
          </label>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 3 }}>
          <Box sx={{ flex: 1, minWidth: '300px' }}>
            <Autocomplete
              size="small"
              sx={{ width: '100%' }}
              options={treks}
              getOptionLabel={(t) => t?.name ?? ''}
              isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
              value={treks.find((t) => String(t.id) === String(trekFilter)) ?? null}
              onChange={(_, trek) => {
                setQuery({ trekId: trek ? String(trek.id) : '', batchId: '', packageIds: '' });
                setIsSubmitClicked(false);
                setSelectedPackageNames([]);
              }}
              noOptionsText="No treks found"
              ListboxProps={{ style: { maxHeight: 320 } }}
              renderInput={(params) => <TextField {...params} label="Select Trek" placeholder="Search trek…" />}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: '300px' }}>
            <Autocomplete
              size="small"
              sx={{ width: '100%' }}
              disabled={!trekFilter}
              loading={batchesLoading}
              options={batches}
              getOptionLabel={(b) => (b ? registrationBatchOptionLabel(b) : '')}
              isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
              value={batches.find((b) => String(b.id) === String(batchFilter)) ?? null}
              onChange={(_, batch) => {
                setQuery({ batchId: batch ? String(batch.id) : '' });
                setIsSubmitClicked(false);
              }}
              noOptionsText={trekFilter ? (batchesLoading ? 'Loading events…' : 'No events found') : 'Select trek first'}
              ListboxProps={{ style: { maxHeight: 320 } }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Departure Date (Upcoming First)"
                  placeholder={trekFilter ? (batchesLoading ? 'Loading events…' : 'Pick departure date…') : 'Select trek first'}
                />
              )}
            />
          </Box>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3, mb: 3 }}>
          <Box>
            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5, display: 'block' }}>Joining Option - Travel Option</Typography>
            <Select
              size="small"
              multiple
              displayEmpty
              value={selectedPackageNames}
              disabled={!isValidTrekFilter || !isValidBatchFilter}
              onChange={(e) => {
                const val = e.target.value;
                if (val.includes('ALL')) {
                   if (selectedPackageNames.length === trekPackages.length) {
                     setSelectedPackageNames([]);
                   } else {
                     setSelectedPackageNames(trekPackages.map(p => String(p.id)));
                   }
                } else {
                   setSelectedPackageNames(typeof val === 'string' ? val.split(',') : val);
                }
                setIsSubmitClicked(false);
              }}
              fullWidth
              sx={{ bgcolor: '#f4f6f8' }}
              renderValue={(selected) => {
                if (selected.length === 0 || selected.length === trekPackages.length) return <Typography variant="body2" sx={{ color: 'text.primary' }}>All Package Options</Typography>;
                return <Typography variant="body2" sx={{ color: 'text.primary' }}>{selected.length} Selected</Typography>;
              }}
              MenuProps={{
                PaperProps: {
                  style: { maxHeight: 400, borderRadius: 12, padding: '8px' }
                }
              }}
            >
              <MenuItem value="ALL" sx={{ bgcolor: 'rgba(239, 68, 68, 0.08)', borderRadius: 2, mb: 1, '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.12)' } }}>
                 <Typography variant="body2" sx={{ color: '#ef4444', fontWeight: 600, flex: 1 }}>Select All (All Package Options)</Typography>
                 {selectedPackageNames.length === trekPackages.length && <span style={{ color: '#ef4444' }}>✓</span>}
              </MenuItem>
              {trekPackages.map((pkg) => {
                const name = packageFilterTitle(pkg);
                const desc = packageFilterDetail(pkg);
                const label = desc ? `${name} - ${desc}` : name;
                const id = String(pkg.id);
                return (
                  <MenuItem key={id} value={id} sx={{ borderRadius: 1 }}>
                    <Checkbox checked={selectedPackageNames.includes(id)} size="small" />
                    <ListItemText primary={label} primaryTypographyProps={{ variant: 'body2', color: '#334155' }} />
                  </MenuItem>
                );
              })}
            </Select>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5, display: 'block' }}>Registration Statuses</Typography>
            <Select
              size="small"
              multiple
              displayEmpty
              value={statusSelections}
              onChange={(e) => {
                const next = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                const cleaned = [...new Set(next.map((s) => String(s).trim().toLowerCase()).filter((s) => ALLOWED_STATUS_FILTER_VALUES.has(s)))];
                setQuery({ status: cleaned.length ? cleaned.join(',') : STATUS_FILTER_ALL });
                setIsSubmitClicked(false);
              }}
              fullWidth
              sx={{ bgcolor: '#f4f6f8' }}
              renderValue={(selected) => {
                if (selected.length === 0) return <Typography variant="body2" sx={{ color: 'text.primary' }}>All Statuses</Typography>;
                return <Typography variant="body2" sx={{ color: 'text.primary' }}>{selected.map(val => REGISTRATION_STATUS_OPTIONS.find(o => o.value === val)?.label || val).join(', ')}</Typography>;
              }}
              MenuProps={{
                PaperProps: {
                  style: { maxHeight: 400, borderRadius: 12, padding: '8px' }
                }
              }}
            >
              {REGISTRATION_STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value} sx={{ borderRadius: 1 }}>
                  <Checkbox checked={statusSelections.includes(opt.value)} size="small" />
                  <ListItemText primary={opt.label} primaryTypographyProps={{ variant: 'body2', color: '#334155' }} />
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
          <Button 
             variant="contained" 
             color="primary" 
             disabled={!isValidTrekFilter || !isValidBatchFilter}
             onClick={() => {
               if (selectedPackageNames.length > 0 && selectedPackageNames.length !== trekPackages.length) {
                 setQuery({ packageIds: selectedPackageNames.join(',') });
               } else {
                 setQuery({ packageIds: '' });
               }
               setIsSubmitClicked(true);
             }}
             sx={{ px: 6, py: 1.5, borderRadius: 8, fontWeight: 'bold', fontSize: '1rem', textTransform: 'none', boxShadow: '0 4px 14px 0 rgba(0,118,255,0.39)' }}
          >
             Show Registrations
          </Button>
        </Box>
      </Card>


      {/* ── Results ──────────────────────────────────── */}
      <Card className="ops-table-card reg-table-card">
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-color)' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Registrations ({pagination.total ?? list.length})
            </Typography>
            {canExportRegistrationsCsv && isFilteredView ? (
              <Button
                variant="outlined"
                size="small"
                disabled={isLoading || exporting}
                onClick={handleExportRegistrations}
                startIcon={<Download size={16} />}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
              >
                {exporting ? 'Preparing…' : 'Download Excel (CSV)'}
              </Button>
            ) : null}
          </Box>
          {showingRecentDefault && !isLoading && list.length > 0 ? (
            <p className="ops-table-hint">
              Showing {list.length} most recent registration{list.length === 1 ? '' : 's'}. Use search or trek +
              event filters to find older bookings.
            </p>
          ) : null}
          {isLoading ? (
            <div className="ops-loading-box">
              <CircularProgress size={32} sx={{ color: 'primary.main' }} />
              <Typography variant="body2" color="text.secondary">Loading registrations…</Typography>
            </div>
          ) : displayRows.length === 0 ? (
            <EmptyState
              title="No registrations found"
              description="Try broadening your search or changing the status filter."
            />
          ) : (
            <div className="reg-table-wrap">
              <table className="table" style={{ minWidth: 1320, whiteSpace: 'nowrap' }}>
                <thead>
                  <tr>
                    <th>
                      <TableSortLabel
                        active={sortBy === 'registrationId'}
                        direction={sortBy === 'registrationId' ? sortOrder : 'asc'}
                        onClick={() => toggleColumnSort('registrationId')}
                      >
                        Reg ID
                      </TableSortLabel>
                    </th>
                    <th>Package</th>
                    <th>Group</th>
                    <th>
                      <TableSortLabel
                        active={sortBy === 'firstName'}
                        direction={sortBy === 'firstName' ? sortOrder : 'asc'}
                        onClick={() => toggleColumnSort('firstName')}
                      >
                        Name
                      </TableSortLabel>
                    </th>
                    <th>Age</th>
                    <th>Gender</th>
                    <th>
                      <TableSortLabel
                        active={sortBy === 'phone'}
                        direction={sortBy === 'phone' ? sortOrder : 'asc'}
                        onClick={() => toggleColumnSort('phone')}
                      >
                        Phone
                      </TableSortLabel>
                    </th>
                    <th>Stay</th>
                    <th>
                      <TableSortLabel
                        active={sortBy === 'status'}
                        direction={sortBy === 'status' ? sortOrder : 'asc'}
                        onClick={() => toggleColumnSort('status')}
                      >
                        Status
                      </TableSortLabel>
                    </th>
                    {showRegistrationActions && <th style={{ textAlign: 'right' }}>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {displayRows.map(({ booking, traveler, travelerIndex, rowKey }, rowIndex) => {
                    const row = booking;
                    const trek = row.batch?.trek;
                    const pkg = resolveBookingPackage(row);
                    const reg = row.registration || null;
                    const displayTraveler = traveler || (Array.isArray(row.travelers) && row.travelers[0] ? row.travelers[0] : null);
                    const displayName = formatPersonDisplayName(
                      displayTraveler?.firstName || reg?.firstName,
                      displayTraveler?.lastName || reg?.lastName
                    );
                    const initials = displayName.split(/\s+/).map((p) => p[0]).join('').toUpperCase().slice(0, 2);
                    const groupFromRegistration = row.registration?.group || null;
                    const groupNameOnly = String(groupFromRegistration?.groupName || '').trim() || String(row.groupName || '').trim() || initials || null;
                    const groupNumber = Number(groupFromRegistration?.id || row.groupNumber || 0) > 0 ? Number(groupFromRegistration?.id || row.groupNumber) : null;
                    const groupName = groupNameOnly ? `${groupNameOnly}${groupNumber ? ` (#${groupNumber})` : ''}` : '—';
                    const primaryGender = formatGenderDisplay(displayTraveler?.gender || reg?.gender);
                    const primaryAge = formatAgeFromBirthdate(primaryTravelerDob(displayTraveler, reg));
                    const primaryPhone = formatPhoneForTableDisplay(displayTraveler?.phone || reg?.phone);
                    const stayOpt = formatBookingStayDisplay(row);
                    const regStatus = getEffectiveRegistrationStatus(
                      row,
                      displayTraveler,
                      travelerIndex,
                      reg?.status
                    );
                    const statusLabel = registrationStatusLabel(regStatus);
                    const pkgDescFull = packageDetailSummary(pkg);
                    const pkgDescPreview = pkgDescFull.length > 160 ? `${pkgDescFull.slice(0, 160).trim()}…` : pkgDescFull;
                    const participantCount = getBookingParticipantCount(row);
                    const paymentState = getParticipantPaymentState(row, displayTraveler, travelerIndex);
                    const { dpnr, rpnr } = resolveTicketPnrs(row);
                    const registrationIdDisplay =
                      row.registrationId != null
                        ? String(row.registrationId)
                        : reg?.id != null
                          ? String(reg.id)
                          : '—';
                    return (
                      <tr key={rowKey}>
                        <td>
                          <Tooltip
                            title={
                              traveler
                                ? `Participant ${travelerIndex + 1} of ${participantCount}`
                                : 'Registration id'
                            }
                            enterDelay={400}
                          >
                            <span className="reg-cell-id">{registrationIdDisplay}</span>
                          </Tooltip>
                        </td>
                        <td style={{ maxWidth: 280, verticalAlign: 'top', whiteSpace: 'normal' }}>
                          <div className="reg-cell-pkg-name">{pkg?.name || '—'}</div>
                          {pkgDescPreview ? (
                            <Tooltip title={pkgDescFull} placement="top" enterDelay={400}>
                              <div className="reg-cell-pkg-desc">{pkgDescPreview}</div>
                            </Tooltip>
                          ) : null}
                        </td>
                        <td className="reg-cell-group">{groupName}</td>
                        <td className="reg-cell-name">{displayName}</td>
                        <td className="reg-cell-muted">{primaryAge}</td>
                        <td className="reg-cell-muted">{primaryGender}</td>
                        <td className="reg-cell-muted">{primaryPhone}</td>
                        <td className="reg-cell-muted">{stayOpt}</td>
                        <td>
                          <RegistrationStatusHover
                            booking={row}
                            traveler={displayTraveler}
                            travelerIndex={travelerIndex}
                            status={regStatus}
                            statusLabel={statusLabel}
                          />
                        </td>
                        {showRegistrationActions && (
                          <td style={{ textAlign: 'right' }}>
                            <div className="reg-actions">
                              {canManageRegistrations ? (
                                <Tooltip title="Edit name, event, package, or status">
                                  <button type="button" className="reg-btn-edit" onClick={() => openEditRegistration(row)}>Edit</button>
                                </Tooltip>
                              ) : null}
                              {canViewTickets &&
                              travelerIndex === 0 &&
                              (bookingInvolvesTickets(row) || bookingHasTicketFiles(row)) ? (
                                <Tooltip title="View uploaded tickets">
                                  <button
                                    type="button"
                                    className="reg-btn-view"
                                    aria-label="View tickets"
                                    onClick={() => openTicketModal(row)}
                                  >
                                    <Eye size={16} />
                                  </button>
                                </Tooltip>
                              ) : null}
                              {canManageRegistrations && !isNonPayingRegistration(row) ? (
                                <Tooltip
                                  title={
                                    isParticipantFullyPaid(row, displayTraveler, travelerIndex)
                                      ? 'This participant is fully paid'
                                      : `Record payment for ${paymentState.participantName || 'participant'} (₹${Math.round(paymentState.pending)} pending)`
                                  }
                                >
                                  <span>
                                    <button
                                      type="button"
                                      className="reg-btn-pay"
                                      disabled={isParticipantFullyPaid(row, displayTraveler, travelerIndex)}
                                      onClick={() =>
                                        openReceiptModal({
                                          booking: row,
                                          traveler: displayTraveler,
                                          travelerIndex
                                        })
                                      }
                                    >
                                      Pay
                                    </button>
                                  </span>
                                </Tooltip>
                              ) : null}
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* ── Table footer ─────────────────────────── */}
          {!isLoading && displayRows.length > 0 ? (
            <div className="reg-table-footer">
              <div className="reg-export-wrap">
                {/* Export button moved to table header */}
              </div>
              <div className="reg-pagination">
                {showingRecentDefault ? (
                  <span className="reg-page-info">
                    Latest {list.length} of {pagination.total ?? list.length} total
                  </span>
                ) : pagination.total != null ? (
                  <>
                    <span className="reg-page-info">
                      {pagination.totalPages > 1
                        ? `Page ${pagination.page} of ${pagination.totalPages} · ${pagination.total} total`
                        : `${pagination.total} total`}
                    </span>
                    {pagination.totalPages > 1 ? (
                      <>
                        <button
                          type="button"
                          className="reg-page-btn"
                          disabled={pagination.page <= 1}
                          onClick={() => setQuery({ page: String(Math.max(1, page - 1)) }, { resetPage: false })}
                        >
                          ← Previous
                        </button>
                        <button
                          type="button"
                          className="reg-page-btn"
                          disabled={pagination.page >= pagination.totalPages}
                          onClick={() =>
                            setQuery({ page: String(Math.min(pagination.totalPages, page + 1)) }, { resetPage: false })
                          }
                        >
                          Next →
                        </button>
                      </>
                    ) : null}
                  </>
                ) : null}
              </div>
            </div>
          ) : null}
      </Card>

      <EditRegistrationModal
        open={editModal.open}
        bookingId={editModal.bookingId}
        onClose={() => setEditModal({ open: false, bookingId: null })}
        onSaved={() => queryClient.invalidateQueries('admin-bookings')}
      />

      <EditTicketGroupModal
        open={ticketModal.open}
        row={ticketModal.row}
        canManage={false}
        onClose={() => setTicketModal({ open: false, row: null })}
        onSaved={() => queryClient.invalidateQueries('admin-bookings')}
      />

      <Dialog
        open={receiptModal.open}
        onClose={() =>
          setReceiptModal({
            open: false,
            booking: null,
            bookingLoading: false,
            participantName: null,
            participantIndex: null,
            participantCount: 1
          })
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add manual receipt</DialogTitle>
        <DialogContent>
          {receiptModal.bookingLoading ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              Loading payment details…
            </Typography>
          ) : null}
          {receiptModal.booking && !receiptModal.bookingLoading && (() => {
            const b = receiptModal.booking;
            const total = bookingFeeTotal(b);
            const advanceInfo = effectiveBookingAdvance({ ...b, totalAmount: total });
            const advance = advanceInfo.advanceAmount;
            const office = Number(b.amountPaidAtOffice ?? 0);
            const online = Number(b.amountPaidOnline ?? 0);
            const paid = office + online;
            const pending = Math.max(0, total - paid);
            const advanceDue = Math.max(0, advance - paid);
            const showAdvanceFill =
              hasPartialAdvanceStructure(total, advance) && advanceDue > 0;
            const showPaymentRuleHint =
              (advanceInfo.isWithinAdvanceWindow && total > 0) ||
              (!advanceInfo.isWithinAdvanceWindow && hasPartialAdvanceStructure(total, advance));
            const participantName = receiptModal.participantName || bookingPrimaryDisplayName(b);
            const participantCount = Number(receiptModal.participantCount) > 0 ? Number(receiptModal.participantCount) : 1;
            const participantLabelSuffix =
              participantCount > 1 && Number.isFinite(receiptModal.participantIndex)
                ? ` (${Number(receiptModal.participantIndex) + 1}/${participantCount})`
                : '';
            const participantPayment = getParticipantPaymentState(
              b,
              null,
              receiptModal.participantIndex ?? 0
            );
            const participantTotal = participantPayment.share;
            const participantAdvance = participantPayment.participantAdvance;
            const participantPaid = participantPayment.paid;
            const participantPending = participantPayment.pending;
            const participantAdvanceDue = participantPayment.advanceDue;
            const showParticipantAdvanceFill =
              hasPartialAdvanceStructure(participantTotal, participantAdvance) && participantAdvanceDue > 0;
            const trekName = b.batch?.trek?.name || '—';
            const batchDates = bookingBatchDateLabel(b);
            return (
              <>
                <Box
                  sx={{
                    mb: 2,
                    p: 1.5,
                    borderRadius: 1,
                    bgcolor: 'action.hover',
                    border: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
                    Participant
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 1.25 }}>
                    {participantName}{participantLabelSuffix}
                  </Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                    Trek
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1.25 }}>
                    {trekName}
                  </Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                    Batch dates
                  </Typography>
                  <Typography variant="body2">{batchDates}</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Registration #{b.registrationId ?? b.registration?.id ?? '—'}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: showPaymentRuleHint ? 0.5 : 2 }}
                >
                  Participant share — Total: {formatAdminInr(participantTotal)} · Advance: {formatAdminInr(participantAdvance)} · Paid:{' '}
                  {formatAdminInr(participantPaid)} · Pending: {formatAdminInr(participantPending)}
                </Typography>
                {advanceInfo.isWithinAdvanceWindow && total > 0 && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                    Departure within {ADVANCE_WINDOW_DAYS} days — full amount is due now.
                  </Typography>
                )}
                {!advanceInfo.isWithinAdvanceWindow &&
                  hasPartialAdvanceStructure(total, advance) && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                      {participantCount > 1
                        ? `Advance ₹5,000 per participant (₹${Math.round(participantAdvance * participantCount).toLocaleString('en-IN')} for ${participantCount}); remaining balance ${REMAINING_DUE_DAYS_BEFORE_DEPARTURE} days before departure.`
                        : `Advance ₹5,000 now; remaining balance ${REMAINING_DUE_DAYS_BEFORE_DEPARTURE} days before departure.`}
                    </Typography>
                  )}
                {(participantPending > 0 || showParticipantAdvanceFill) && (
                  <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                    {participantPending > 0 && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() =>
                          setReceiptForm((p) => ({ ...p, amount: String(Math.round(participantPending)) }))
                        }
                      >
                        Fill participant pending ({formatAdminInr(participantPending)})
                      </Button>
                    )}
                    {showParticipantAdvanceFill && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() =>
                          setReceiptForm((p) => ({ ...p, amount: String(Math.round(participantAdvanceDue)) }))
                        }
                      >
                        Fill participant advance ({formatAdminInr(participantAdvanceDue)})
                      </Button>
                    )}
                  </Box>
                )}
              </>
            );
          })()}
          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
            <InputLabel>Payment method</InputLabel>
            <Select
              label="Payment method"
              value={receiptForm.method}
              onChange={(e) => setReceiptForm((p) => ({ ...p, method: e.target.value }))}
            >
              {RECEIPT_PAYMENT_METHOD_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Amount (₹)"
            type="number"
            value={receiptForm.amount}
            onChange={(e) => setReceiptForm((p) => ({ ...p, amount: e.target.value }))}
            margin="normal"
            inputProps={{ min: 0, step: 1 }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 0.5 }}>
            Receipt date: <strong>{receiptModal.booking ? formatDate(new Date().toISOString().slice(0, 10)) : '—'}</strong>{' '}
            (today)
          </Typography>
          <TextField
            fullWidth
            label="Notes"
            multiline
            rows={2}
            value={receiptForm.notes}
            onChange={(e) => setReceiptForm((p) => ({ ...p, notes: e.target.value }))}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setReceiptModal({
                open: false,
                booking: null,
                bookingLoading: false,
                participantName: null,
                participantIndex: null,
                participantCount: 1
              })
            }
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={submitReceipt} disabled={addReceiptMutation.isLoading || receiptModal.bookingLoading || !receiptForm.amount}>
            {addReceiptMutation.isLoading ? 'Saving...' : 'Save receipt'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Registrations;
